import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  SxProps,
  Grid2,
  Stack,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { BLUE, GRAY, PasswordErrMsg, WHITE } from "../utils/constants";
import { createPassword, isUserPasswordCreated } from "../api/requests/users";
import AppLogoViolet from "../assets/AppLogo.png";
import { useAlert } from "../context/AlertContext";
import { ComputerOutlined } from "@mui/icons-material";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { commonStrings } from "../static/commonStrings";
import { PasswordCriteriaMarking, passwordObj } from "./CheckPasswordCriteria";

const SignUp: React.FC = () => {
  const { showAlert } = useAlert();
  const [loader, setLoader] = useState<boolean>(true);
  const [isPasswordCreated, setIsPasswordCreated] = useState<boolean>(false);
  const [isLinkValid, setIsLinkValid] = useState<boolean | null>(null);
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const userKey = searchParams.get("token");
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
    strongPassword: "",
  });
  const [passwordStatus, setPasswordStatus] = useState(""); // New state for success message
  const [statusText, setStatusText] = useState("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const getErrormessage = async (passwordCheckObj: passwordObj) => {
    const { upper, lower, num, min, special } = passwordCheckObj;
    let errMsg = "";
    let isStrongPassword = false;
    if (!min) {
      errMsg = PasswordErrMsg.minCharacter;
      isStrongPassword = false;
    } else if (!upper) {
      errMsg = PasswordErrMsg.upperCase;
      isStrongPassword = false;
    } else if (!lower) {
      errMsg = PasswordErrMsg.lowerCase;
      isStrongPassword = false;
    } else if (!num) {
      errMsg = PasswordErrMsg.digits;
      isStrongPassword = false;
    } else if (!special) {
      errMsg = PasswordErrMsg.specialChar;
      isStrongPassword = false;
    } else {
      errMsg = "";
      isStrongPassword = true;
    }
    return { isStrongPassword, errMsg };
  };

  useEffect(() => {
    setLoader(true);
    const fetchUserStatus = async () => {
      try {
        const userStatus = await isUserPasswordCreated({
          token: userKey ?? "",
        });
        if (userStatus.status === 200) {
          if (userStatus.data.isUserDeleted) {
            setIsLinkValid(true);
            setIsPasswordCreated(false);
          } else if (userStatus.data.isEmailConfirmed) {
            setIsLinkValid(false);
            setStatusText("Password Already Created, Sign in with ");
            setIsPasswordCreated(true);
          } else {
            setIsLinkValid(false);
            setIsPasswordCreated(false);
          }
        } else {
          showAlert("Failed to fetch user status.", "error");
        }
      } catch {
        showAlert("An error occurred while fetching user status.", "error");
      } finally {
        setLoader(false);
      }
    };
    fetchUserStatus();
  }, [userKey, showAlert]);

  if (loader) {
    return (
      <Container maxWidth="sm" sx={loaderContainerStyles}>
        <CircularProgress size={60} thickness={6} />
      </Container>
    );
  }

  if (isLinkValid) {
    return (
      <Container maxWidth="sm" sx={linkExpiredBoxSx}>
        <Box sx={linkExpiredContentBoxSx}>
          <img
            src="https://cdn-icons-png.flaticon.com/512/753/753345.png"
            alt="Invalid Link"
            width={80}
            height={80}
            style={{ marginBottom: 24 }}
          />
          <Typography variant="h5" fontWeight="bold" gutterBottom color="error">
            {commonStrings.signUp.notValid}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {commonStrings.signUp.linkExpired}
          </Typography>
        </Box>
      </Container>
    );
  }

  const redirectToLogin = () => {
    return (
      <Box sx={redirectionBoxStyles}>
        <Typography variant="h5" color="green" mr={0.5}>
          {statusText}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Box display="flex" alignItems="center">
            <SmartphoneIcon sx={{ fontSize: 30, color: "green", mr: 0.5 }} />
            <Typography variant="h5" color="green">
              Mobile
            </Typography>
          </Box>
          <Typography sx={{ ml: 1 }} variant="h5" color="green">
            Or
          </Typography>
          <Box display="flex" alignItems="center">
            <ComputerOutlined sx={{ fontSize: 30, color: "green", mr: 0.5 }} />
            <Typography variant="h5" color="green">
              Web Application
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  };

  const validatePasswords = async (): Promise<boolean> => {
    const newPassword = newPasswordRef.current?.value;
    const confirmPassword = confirmPasswordRef.current?.value;
    let valid = true;
    const newErrors = {
      newPassword: "",
      confirmPassword: "",
      strongPassword: "",
    };
    setPasswordStatus("");
    if (!newPassword) {
      newErrors.newPassword = "Enter new password";
      valid = false;
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    } else {
      const passwordCheckResult = await PasswordCriteriaMarking(newPassword);
      const result = await getErrormessage(passwordCheckResult);
      if (result.isStrongPassword) {
        setPasswordStatus(PasswordErrMsg.strongPassword);
      } else {
        newErrors.strongPassword = result.errMsg;
        setPasswordStatus("");
        valid = false;
      }
    }
    setErrors(newErrors);

    return valid;
  };

  const handlePasswordCreation = async () => {
    if (await validatePasswords()) {
      try {
        const response = await createPassword({
          token: userKey!,
          newPassword: newPasswordRef.current?.value ?? "",
        });
        if (response.status === 200) {
          setStatusText("Password Created Successfully, Sign in with");
          setIsPasswordCreated(true);
          showAlert("Password created successfully!", "success");
        } else {
          showAlert(
            response?.data?.message || "Failed to create password.",
            "error"
          );
        }
      } catch (error) {
        showAlert("An error occurred while creating the password.", "error");
      }
    }
  };

  if (isPasswordCreated) {
    return redirectToLogin();
  }

  return (
    <Box sx={outerBoxStyles}>
      <Box sx={innerBoxStyles}>
        <Grid2 container>
          <Grid2 size={6} sx={leftGridStyles}>
            <Box sx={leftBoxStyles}>
              <img
                src={AppLogoViolet}
                alt="App Logo"
                style={{ width: 200, height: 200 }}
              />
              <Typography variant="h5" sx={titleStyles}>
                Sign up
              </Typography>
              <Typography variant="body1" sx={subtitleStyles}>
                Sign up to continue to Chat.
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <Box sx={BoxContainer}>
              <Typography variant="body1" sx={{ ...labelStyles, mt: 4 }}>
                Create Your Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your new password"
                inputRef={newPasswordRef}
                type={showNewPassword ? "text" : "password"}
                sx={textFieldStyles}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body1" sx={{ ...labelStyles, mt: 4 }}>
                Re-enter Your Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Re-enter new password"
                inputRef={confirmPasswordRef}
                type={showConfirmPassword ? "text" : "password"}
                sx={textFieldStyles}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.strongPassword && (
                <Typography variant="body2" sx={{ color: "red" }}>
                  {errors.strongPassword}
                </Typography>
              )}
              {passwordStatus && (
                <Typography variant="body2" sx={{ color: "green" }}>
                  {passwordStatus}
                </Typography>
              )}
              <Button
                onClick={handlePasswordCreation}
                fullWidth
                variant="contained"
                sx={buttonStyles}
              >
                Create Password
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default SignUp;

const redirectionBoxStyles: SxProps = {
  height: "100vh",
  width: "100vw",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const loaderContainerStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "inherit",
};

const outerBoxStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  height: "inherit",
  width: "inherit",
  bgcolor: WHITE.moderate,
  p: 4,
  boxSizing: "border-box",
};

const innerBoxStyles: SxProps = {
  flexGrow: 1,
  height: "100%",
  bgcolor: WHITE.main,
  borderRadius: "24px",
  alignContent: "center",
};

const leftGridStyles: SxProps = {
  alignContent: "center",
};

const leftBoxStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  ml: "89px",
};

const titleStyles: SxProps = {
  fontSize: "58.45px",
  fontFamily: "Poppins, sans-serif",
};

const subtitleStyles: SxProps = {
  color: GRAY.dark,
  fontSize: "32.88px",
  fontFamily: "Poppins, sans-serif",
};

const labelStyles: SxProps = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "23.8px",
};

const textFieldStyles: SxProps = {
  height: "69.69999694824219px",
  bgcolor: GRAY.light,
  "& .MuiOutlinedInput-root": {
    borderRadius: "11px",
    fontFamily: "Poppins, sans-serif",
    height: "69.69999694824219px",
    "& fieldset": {
      borderColor: "transparent",
    },
  },
  borderRadius: "11px",
};

const buttonStyles: SxProps = {
  bgcolor: BLUE.dark,
  mt: 6,
  height: "69.70000457763672px",
  borderRadius: "13.33px",
  fontSize: "27.2px",
  textTransform: "none",
};

export const BoxContainer: SxProps = {
  width: "510px",
  minHeight: "300px",
  p: 4,
  m: 8,
  borderRadius: "20px",
  justifyContent: "space-evenly",
};

const linkExpiredBoxSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  textAlign: "center",
  padding: 4,
};

const linkExpiredContentBoxSx: SxProps = {
  p: 4,
  borderRadius: 4,
  backgroundColor: "#fff",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  maxWidth: 480,
  width: "100%",
};
